import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider, MutationCache } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router';
import AuthGate from './features/auth/AuthGate';
import ErrorBoundary from './components/ErrorBoundary';
import PortalPage from './features/portal/PortalPage';
import ShowcasePage from './features/portal/ShowcasePage';
import QuoteApprovalPage from './features/portal/QuoteApprovalPage';
import RentalCatalogPage from './features/rental/RentalCatalogPage';
import MobilePickupPage from './features/rental/MobilePickupPage';
import UnitPage from './features/propUnits/UnitPage';
import { initTheme } from './lib/theme';
import { Toaster, toast } from './lib/toast';
import { initPwa } from './lib/pwa';
import { outbox } from './lib/offline';
import { SaveCancelled } from './lib/safeSave';
import { ApiError } from './lib/api';
import { ME_KEY } from './features/auth/api';
import '@fontsource-variable/inter';
import './index.css';

initTheme();

// Hlášení říká, co se opravdu stalo: co odešlo, co server odmítl a co ještě čeká.
async function sendOutbox() {
  const { sent, refused, waiting } = await outbox.flush();
  if (sent > 0) toast.success(`Odložené změny odeslány: ${sent}`);
  for (const why of refused) toast.error(`Odložená změna neprošla: ${why}`);
  return waiting;
}

// PWA: service worker, online/offline, a přehrání offline fronty po obnovení připojení.
initPwa(() => { void sendOutbox(); });
outbox.whenUserKnown(() => { if (navigator.onLine) void sendOutbox(); });
// Událost online přijde dřív, než síť opravdu běží. Co neodešlo, se zkouší znovu.
setInterval(() => { if (navigator.onLine && outbox.size() > 0) void sendOutbox(); }, 20_000);

// Pojistka proti „rozbitým položkám" po nasazení nové verze: když selže načtení
// lazy chunku (starý index odkazuje na už neexistující soubor), jednou obnovíme
// stránku — prohlížeč si stáhne čerstvý index. Guard proti reload smyčce.
window.addEventListener('vite:preloadError', (e) => {
  if (sessionStorage.getItem('chunk-reloaded') !== '1') {
    sessionStorage.setItem('chunk-reloaded', '1');
    e.preventDefault();
    window.location.reload();
  }
});
window.addEventListener('load', () => sessionStorage.removeItem('chunk-reloaded'));

// Veřejné stránky běží mimo aplikaci (bez loginu, sidebaru i routeru):
// /portal/ = klientský náhled zakázky, /vyloha/ = veřejná výloha výroby (QR).
const isPortal = window.location.pathname.startsWith('/portal/');
const isShowcase = window.location.pathname.startsWith('/vyloha/');
const isCatalog = window.location.pathname.startsWith('/pujcovna-katalog');
const isMobilePickup = window.location.pathname.startsWith('/pujcovna/m/');
const isUnit = window.location.pathname.startsWith('/kus/');
// /nabidka/ = cenová nabídka ke schválení klientem (přijmout/odmítnout + podpis).
const isQuoteApproval = window.location.pathname.startsWith('/nabidka/');
// Schválení nabídky má jen světlý vzhled, jako papírová nabídka. Tmavý systém klienta ho nesmí rozbít.
if (isQuoteApproval) document.documentElement.classList.remove('dark');

const queryClient: QueryClient = new QueryClient({
  // Relace skončila (změna hesla na jiném zařízení, vypršení): zpět na přihlášení, ne chyby na každé obrazovce.
  queryCache: new QueryCache({
    onError: (err, query) => {
      if (err instanceof ApiError && err.status === 401 && query.queryKey[0] !== ME_KEY[0]) void queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  }),
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: true, retry: 1 },
    // Zápis se offline nesmí tiše pozastavit: buď jde do fronty, nebo člověk dostane hlášku.
    mutations: { networkMode: 'always' },
  },
  // Jednotné chybové hlášky pro všechny mutace na jednom místě.
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => {
      if (err instanceof SaveCancelled) return; // člověk se rozhodl vrátit k úpravám
      if (mutation.meta?.quiet) return; // obrazovka chybu ukazuje sama
      const msg = err instanceof Error && err.message ? err.message : 'Akce se nezdařila';
      toast.error(msg);
    },
  }),
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary scope="aplikace">
      {isPortal ? (
        <PortalPage />
      ) : isShowcase ? (
        <ShowcasePage />
      ) : isCatalog ? (
        <RentalCatalogPage />
      ) : isMobilePickup ? (
        <MobilePickupPage />
      ) : isUnit ? (
        <UnitPage />
      ) : isQuoteApproval ? (
        <QuoteApprovalPage />
      ) : (
        <AuthGate>
          <RouterProvider router={router} />
        </AuthGate>
      )}
      </ErrorBoundary>
      <Toaster />
    </QueryClientProvider>
  </React.StrictMode>,
);
